
import { useState, useEffect, useRef } from 'react';

const SEARCH_CATEGORIES = [
  { type: 'hospital',      label: 'Hospital'     },
  { type: 'school',        label: 'School'       },
  { type: 'restaurant',    label: 'Restaurant'   },
  { type: 'gym',           label: 'Gym'          },
  { type: 'shopping_mall', label: 'Mall'         },
  { type: 'park',          label: 'Park'         },
  { type: 'subway_station',label: 'Metro'        },
  { type: 'supermarket',   label: 'Supermarket'  },
  { type: 'cafe',          label: 'Cafe'         },
  { type: 'hindu_temple',  label: 'Temple'       },
];

const RADIUS_METERS   = 5000;
const MAX_PER_CATEGORY = 3;
const PHOTO_MAX_WIDTH  = 600;

function haversineKm(lat1, lng1, lat2, lng2) {
  const R    = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDistanceKm(km) {
  return km < 1
      ? `${Math.round(km * 1000)} m`
      : `${km.toFixed(1)} km`;
}

function photoUrl(photoReference, apiKey) {
  return (
      `https://maps.googleapis.com/maps/api/place/photo` +
      `?maxwidth=${PHOTO_MAX_WIDTH}` +
      `&photoreference=${photoReference}` +
      `&key=${apiKey}`
  );
}

/**
 * Safely extract a photo URL from a Places API photos array.
 *
 * The JS Places SDK returns photo objects with a `.getUrl()` method.
 * The REST / serialised responses return a raw `photo_reference` string.
 * This helper handles both cases cleanly.
 */
function getPlacePhotoUrl(photos, apiKey) {
  if (!photos || photos.length === 0) return null;
  const photo = photos[0];

  if (typeof photo.getUrl === 'function') {
    try {
      return photo.getUrl({ maxWidth: PHOTO_MAX_WIDTH });
    } catch {
      return null;
    }
  }

  if (photo.photo_reference) {
    return photoUrl(photo.photo_reference, apiKey);
  }

  return null;
}

const CACHE_PREFIX = 'ogm_nearby_v2_';

function cacheGet(lat, lng) {
  try {
    const key  = `${CACHE_PREFIX}${lat.toFixed(4)}_${lng.toFixed(4)}`;
    const raw  = sessionStorage.getItem(key);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > 30 * 60 * 1000) { sessionStorage.removeItem(key); return null; }
    return data;
  } catch { return null; }
}

function cacheSet(lat, lng, data) {
  try {
    const key = `${CACHE_PREFIX}${lat.toFixed(4)}_${lng.toFixed(4)}`;
    sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch { /* sessionStorage full — silent */ }
}

export default function useNearbyPlaces(lat, lng) {
  const [places,  setPlaces]  = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!lat || !lng || isNaN(lat) || isNaN(lng)) return;

    cancelledRef.current = false;

    const cached = cacheGet(lat, lng);
    if (cached) {
      setPlaces(cached);
      setLoading(false);
      return;
    }

    const waitForSdk = () =>
        new Promise((resolve, reject) => {
          if (window.google?.maps?.places?.PlacesService) { resolve(); return; }
          let attempts = 0;
          const t = setInterval(() => {
            if (window.google?.maps?.places?.PlacesService) { clearInterval(t); resolve(); }
            if (++attempts > 50) { clearInterval(t); reject(new Error('Maps SDK timeout')); }
          }, 200);
        });

    const run = async () => {
      setLoading(true);
      setError(null);

      try {
        await waitForSdk();
        if (cancelledRef.current) return;

        const apiKey   = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        const location = new window.google.maps.LatLng(lat, lng);

        const dummyDiv = document.createElement('div');
        dummyDiv.style.display = 'none';
        document.body.appendChild(dummyDiv);
        const service  = new window.google.maps.places.PlacesService(dummyDiv);

        const searchCategory = ({ type, label }) =>
            new Promise((resolve) => {
              service.nearbySearch(
                  {
                    location,
                    radius:  RADIUS_METERS,
                    type,
                  },
                  (results, status) => {
                    const S = window.google.maps.places.PlacesServiceStatus;
                    if (status !== S.OK && status !== S.ZERO_RESULTS) {
                      console.warn(`Places search failed for ${type}:`, status);
                      resolve([]);
                      return;
                    }

                    const places = (results || [])
                        .slice(0, MAX_PER_CATEGORY)
                        .map((place, idx) => {
                          const placeLat = place.geometry.location.lat();
                          const placeLng = place.geometry.location.lng();
                          const distKm   = haversineKm(lat, lng, placeLat, placeLng);

                          return {
                            id:       place.place_id,
                            name:     place.name,
                            category: label,
                            distance: formatDistanceKm(distKm),
                            latitude:  placeLat,
                            longitude: placeLng,
                            rating:   place.rating || null,
                            address:  place.vicinity || null,
                            isOpen:   place.opening_hours?.isOpen?.() ?? null,
                            image:    getPlacePhotoUrl(place.photos, apiKey),
                          };
                        });

                    resolve(places);
                  }
              );
            });

        const allResults = await Promise.all(
            SEARCH_CATEGORIES.map(searchCategory)
        );

        if (cancelledRef.current) return;

        const seen    = new Set();
        const merged  = allResults
            .flat()
            .filter((p) => {
              if (seen.has(p.id)) return false;
              seen.add(p.id);
              return true;
            })
            .map((p, i) => ({ ...p, _index: i }));

        cacheSet(lat, lng, merged);
        setPlaces(merged);
        if (dummyDiv.parentNode) dummyDiv.parentNode.removeChild(dummyDiv);
      } catch (err) {
        if (dummyDiv.parentNode) dummyDiv.parentNode.removeChild(dummyDiv);
        if (!cancelledRef.current) {
          console.error('useNearbyPlaces error:', err);
          setError(err.message);
        }
      } finally {
        if (!cancelledRef.current) setLoading(false);
      }
    };

    run();
    return () => { cancelledRef.current = true; };
  }, [lat, lng]);

  return { places, loading, error };
}
